import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, BookOpen, Volume2, Sparkles, Zap } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

const METER_ROWS = [
  { key: "messages", label: "Messages", icon: MessageSquare, packCat: "messages", defaultPack: "boost_200" },
  { key: "stories", label: "Stories", icon: BookOpen, packCat: "stories", defaultPack: "stories_20" },
  { key: "wisdom_tts", label: "Wisdom Voice", icon: Sparkles, packCat: "wisdom", defaultPack: "wisdom_25" },
  { key: "voice_tts", label: "Chat Voice", icon: Volume2, packCat: "voice", defaultPack: "voice_50" },
];

export const UsageMeter = ({ compact = false }) => {
  const { token } = useAuth();
  const [usage, setUsage] = useState(null);
  const [buying, setBuying] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchUsage = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/usage/summary`, { headers });
      setUsage(data);
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  const buyPack = async (packId) => {
    setBuying(packId);
    try {
      const { data } = await axios.post(`${API}/topups/checkout`, { pack_id: packId, origin_url: window.location.origin }, { headers });
      if (data.status === "redirect" && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (data.status === "credited") {
        toast.success(data.message);
        fetchUsage();
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Top-up failed"); }
    finally { setBuying(null); }
  };

  const toggleAutoTopup = async (enabled) => {
    try {
      const { data } = await axios.post(`${API}/usage/auto-topup`, { enabled, pack_id: usage?.auto_topup?.pack_id || "boost_200" }, { headers });
      setUsage(u => ({ ...u, auto_topup: data.auto_topup }));
      toast.success(enabled ? "Auto top-up enabled — you'll never hit a wall mid-conversation" : "Auto top-up disabled");
    } catch { toast.error("Could not update setting"); }
  };

  if (!usage || usage.tier === "team_dev" || usage.legacy_pricing) return null;

  const barColor = (pct) => pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500";

  return (
    <div data-testid="usage-meter" className={cn("rounded-2xl border border-border/50 bg-card", compact ? "p-4" : "p-5")}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-sm font-medium flex items-center gap-2">
          <Zap className="w-4 h-4 text-primary" strokeWidth={1.5} /> Monthly Usage — {usage.tier_name}
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground">Auto top-up</span>
          <Switch data-testid="auto-topup-toggle" checked={!!usage.auto_topup?.enabled} onCheckedChange={toggleAutoTopup} />
        </div>
      </div>
      <div className="space-y-3">
        {METER_ROWS.map(({ key, label, icon: Icon, defaultPack }) => {
          const row = usage[key];
          if (!row) return null;
          const unlimited = row.allowance === -1 || row.allowance === 0;
          const nearCap = !unlimited && row.pct >= 80;
          return (
            <div key={key} data-testid={`usage-row-${key}`}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {label}
                  {row.rollover > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 font-medium">+{row.rollover} rollover</span>}
                  {row.credits > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">+{row.credits} credits</span>}
                </span>
                <span className="font-medium">
                  {unlimited ? (key === "voice_tts" && !row.plan ? "No plan" : `${row.used} used`) : `${row.used} / ${row.allowance}`}
                </span>
              </div>
              {!unlimited && (
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className={cn("h-full rounded-full transition-all", barColor(row.pct))} style={{ width: `${Math.min(100, row.pct)}%` }} />
                </div>
              )}
              {nearCap && (
                <button
                  data-testid={`topup-prompt-${key}`}
                  onClick={() => buyPack(defaultPack)}
                  disabled={buying === defaultPack}
                  className="mt-1 text-[10px] text-primary font-medium hover:underline"
                >
                  {row.pct >= 100 ? "Limit reached — " : "Almost there — "}top up {usage.top_ups?.[defaultPack]?.name} (${usage.top_ups?.[defaultPack]?.price})
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
